import { createFileRoute, Link, notFound, Outlet, useLocation, useParams, useNavigate } from "@tanstack/react-router";
import { Calendar, MapPin, Users, Ticket, ArrowLeft, Clock, Check, BarChart3, Banknote, Wallet, CreditCard, Copy, CheckCircle, Trash2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatNaira } from "@/data/venues";
import { TestModeBanner } from "@/components/TestModeBanner";
import { eventsApi, walletApi, venuesApi, uploadApi, type EventDetailResponse, type EventTierResponse, type VenueResponse } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const Route = createFileRoute("/events/$eventId")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      edit: search.edit === true || search.edit === "true" || undefined,
    };
  },
  loader: async ({ params }) => {
    try {
      const event = await eventsApi.getById(params.eventId);
      return { event };
    } catch {
      throw notFound();
    }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { event } = loaderData;
    return {
      meta: [
        { title: `${event.name} — OyoBooking Events` },
        { name: "description", content: event.description },
      ],
    };
  },
  component: EventDetailPage,
});

const TIER_LABELS: Record<string, string> = {
  regular: "Regular",
  standard: "Standard",
  early_bird: "Early Bird",
  vip: "VIP",
  vvip: "VVIP",
  table: "Table",
};

const TIER_COLORS: Record<string, string> = {
  regular: "border-blue-200 bg-blue-50",
  standard: "border-slate-200 bg-slate-50",
  early_bird: "border-rose-200 bg-rose-50",
  vip: "border-amber-200 bg-amber-50",
  vvip: "border-purple-200 bg-purple-50",
  table: "border-green-200 bg-green-50",
};
const TIER_BADGE: Record<string, string> = {
  regular: "bg-blue-100 text-blue-700",
  standard: "bg-slate-100 text-slate-700",
  early_bird: "bg-rose-100 text-rose-700",
  vip: "bg-amber-100 text-amber-700",
  vvip: "bg-purple-100 text-purple-700",
  table: "bg-green-100 text-green-700",
};

export function EventDetailPage() {
  const { event: loaderEvent } = Route.useLoaderData();
  const { eventId } = useParams({ from: "/events/$eventId" });
  const [freshEvent, setFreshEvent] = useState<EventDetailResponse | null>(null);
  const event = freshEvent ?? loaderEvent;

  // Reset locally-stored fresh data when navigating to a different event
  useEffect(() => {
    setFreshEvent(null);
  }, [eventId]);

  const { isAdmin, isBusinessOwner, isModerator, user, isAuthenticated } = useAuth();
  const location = useLocation();
  const isTicketsRoute = location.pathname.endsWith("/tickets");

  if (isTicketsRoute) {
    return <Outlet />;
  }
  const search = Route.useSearch();
  const navigate = useNavigate();
  const isEditing = !!search.edit;

  const setIsEditing = (open: boolean) => {
    navigate({
      to: "/events/$eventId",
      params: { eventId: event.id },
      search: { edit: open ? true : undefined },
    });
  };

  const [submitting, setSubmitting] = useState(false);
  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [editForm, setEditForm] = useState({
    name: "",
    organizer: "",
    description: "",
    date: "",
    time: "",
    venueId: "",
    venueName: "",
    image: "",
  });
  const [editTiers, setEditTiers] = useState<
    { id?: string; name: string; tierType: string; price: string; capacity: string; sold: number }[]
  >([]);

  useEffect(() => {
    if (isEditing) {
      setEditForm({
        name: event.name,
        organizer: event.organizer,
        description: event.description ?? "",
        date: event.date ? new Date(event.date).toISOString().split("T")[0] : "",
        time: event.time ?? "",
        venueId: event.venueId ?? "",
        venueName: event.venueName,
        image: event.image ?? "",
      });
      setEditTiers(
        event.tiers?.map((t: EventTierResponse) => ({
          id: t.id,
          name: t.name,
          tierType: t.tierType,
          price: String(t.price),
          capacity: String(t.capacity),
          sold: t.sold,
        })) ?? []
      );
    }
  }, [isEditing, event]);

  useEffect(() => {
    if (isEditing && user?.id) {
      setVenuesLoading(true);
      venuesApi.myVenues(user.id)
        .then(res => setVenues(res.data))
        .catch(() => setVenues([]))
        .finally(() => setVenuesLoading(false));
    }
  }, [isEditing, user?.id]);

  const updateEditTier = (index: number, field: "name" | "tierType" | "price" | "capacity", value: string) => {
    setEditTiers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const removeEditTier = (index: number) => {
    setEditTiers(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setEditForm(f => ({ ...f, image: res.url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    }
  };

  const handleEditSubmit = async () => {
    if (!editForm.name.trim()) return toast.error("Event name is required");
    if (!editForm.organizer.trim()) return toast.error("Organizer name is required");
    if (!editForm.date) return toast.error("Event date is required");
    if (!editForm.venueName.trim()) return toast.error("Venue name is required");

    const invalidTier = editTiers.find(t => !t.name.trim() || !t.price || !t.capacity);
    if (invalidTier) return toast.error("Please fill in all ticket tier details");

    const underCapacityTier = editTiers.find(t => t.sold > 0 && Number(t.capacity) < t.sold);
    if (underCapacityTier) return toast.error(`Capacity for ${underCapacityTier.name} must be at least ${underCapacityTier.sold}`);

    setSubmitting(true);
    try {
      const eventDate = new Date(editForm.date);
      const [hours, minutes] = editForm.time ? editForm.time.split(":").map(Number) : [0, 0];
      eventDate.setHours(hours, minutes);

      await eventsApi.update(event.id, {
        name: editForm.name.trim(),
        organizer: editForm.organizer.trim(),
        description: editForm.description.trim() || undefined,
        date: eventDate.toISOString(),
        time: editForm.time || undefined,
        venueId: editForm.venueId || undefined,
        venueName: editForm.venueName.trim(),
        image: editForm.image || undefined,
        tiers: editTiers.map(t => ({
          id: t.id,
          name: t.name.trim(),
          tierType: t.tierType,
          price: Number(t.price) || 0,
          capacity: Number(t.capacity) || 0,
        })),
      });
      const updatedEvent = await eventsApi.getById(event.id);
      setFreshEvent(updatedEvent);
      toast.success("Event updated successfully!");
      setIsEditing(false);
      navigate({ to: "/events/$eventId", params: { eventId: event.id }, search: { edit: undefined }, replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update event";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isStaff = isAdmin || isBusinessOwner || isModerator;
  const isActive = event.status.toLowerCase() === "active";
  const isEventSoldOut = event.tiers && event.tiers.length > 0 && event.tiers.every((t: EventTierResponse) => t.status.toLowerCase() === "sold_out");
  const [selectedTierId, setSelectedTierId] = useState<string | null>(
    event.tiers?.find((t: EventTierResponse) => t.status.toLowerCase() !== "sold_out")?.id || null
  );
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", email: "" });
  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "bank_transfer">("bank_transfer");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [paymentResult, setPaymentResult] = useState<{
    paymentStatus: string;
    paymentInstruction: string | null;
    dedicatedBank: { accountNumber: string; accountName: string; bankName: string } | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const selectedTier = event.tiers?.find((t: EventTierResponse) => t.id === selectedTierId);
  const total = selectedTier ? selectedTier.price * qty : 0;
  const dateObj = new Date(event.date);

  useEffect(() => {
    if (isAuthenticated) {
      walletApi.getWallet().then(w => setWalletBalance(w.balance)).catch(() => setWalletBalance(null));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name, email: user.email });
    }
  }, [user]);

  const handleRegister = async () => {
    if (!selectedTier) return toast.error("Please select a ticket tier");
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Please enter a valid email");

    setRegistering(true);
    try {
      const res = await eventsApi.register(event.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        tierId: selectedTier.id,
        quantity: qty,
        paymentMethod: isAuthenticated ? paymentMethod : "bank_transfer",
      });

      setPaymentResult({
        paymentStatus: res.paymentStatus,
        paymentInstruction: res.paymentInstruction,
        dedicatedBank: res.dedicatedBank,
      });
      setSuccess(true);

      // Save event booking to localStorage for the user
      if (user) {
        try {
          const key = `oyo_event_bookings_${user.id}`;
          const existing = localStorage.getItem(key);
          const list = existing ? JSON.parse(existing) : [];
          
          const newBooking = {
            id: res.id || `${event.id}_${Date.now()}`,
            eventId: event.id,
            eventName: event.name,
            eventLocation: event.venueName || "Oyo State",
            eventDate: event.date,
            totalAmount: res.totalAmount || total,
            paymentStatus: res.paymentStatus || "pending",
            ticketCode: res.ticketCode,
            quantity: qty,
            tierName: selectedTier.name,
            createdAt: new Date().toISOString(),
            dedicatedBank: res.dedicatedBank,
            paymentInstruction: res.paymentInstruction
          };
          
          const updatedList = [newBooking, ...list.filter((b: any) => b.id !== newBooking.id)];
          localStorage.setItem(key, JSON.stringify(updatedList));
        } catch (e) {
          console.error("Failed to save event booking to local storage", e);
        }
      }

      if (res.paymentStatus === "paid") {
        toast.success("Payment successful! You're registered.");
      } else {
        toast.success("Registration pending. Please complete payment.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Registration failed";
      toast.error(msg);
    } finally {
      setRegistering(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  if (success) {
    const isPaid = paymentResult?.paymentStatus === "paid";
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className={`size-20 rounded-full flex items-center justify-center mx-auto ${isPaid ? "bg-green-100" : "bg-amber-100"}`}>
            {isPaid ? <Check className="size-10 text-green-600" /> : <CheckCircle className="size-10 text-amber-600" />}
          </div>
          <h2 className="font-display text-2xl font-bold">
            {isPaid ? "You're registered!" : "Registration pending"}
          </h2>
          <p className="text-muted-foreground">
            <strong>{qty}× {selectedTier?.name}</strong> for <strong>{event.name}</strong>.<br />
            Total: <strong>{formatNaira(total)}</strong>. Ticket will be sent to {form.email}.
          </p>

          {!isPaid && paymentResult?.dedicatedBank && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left space-y-3">
              <p className="text-sm font-semibold text-amber-800">Complete your payment via bank transfer:</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Bank</span>
                  <span className="font-medium">{paymentResult.dedicatedBank.bankName}</span>
                </div>
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Account Name</span>
                  <span className="font-medium">{paymentResult.dedicatedBank.accountName}</span>
                </div>
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium font-mono">{paymentResult.dedicatedBank.accountNumber}</span>
                    <button onClick={() => copyToClipboard(paymentResult.dedicatedBank!.accountNumber)} className="text-primary hover:text-primary/80">
                      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatNaira(total)}</span>
                </div>
              </div>
              <p className="text-xs text-amber-700">
                Once you transfer the exact amount, your ticket will be confirmed automatically.
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2">
            <Link to="/events"><Button variant="outline" className="rounded-full">Browse more events</Button></Link>
            <Link to="/events/$eventId/tickets" params={{ eventId: event.id }}>
              <Button className="rounded-full">View my tickets</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-border/50 bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Link to="/events" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="size-4" /> All Events
          </Link>
        </div>
      </div>

      {/* Hero image */}
      <div className="relative h-56 sm:h-72 overflow-hidden bg-muted">
        {event.image ? (
          <img src={resolveMediaUrl(event.image)} alt={event.name} className="size-full object-cover" />
        ) : (
          <div className="size-full bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center">
            <Ticket className="size-20 text-primary/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          {isEventSoldOut ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 text-xs font-semibold text-white">
              SOLD OUT
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white ${
              event.status === "active" ? "bg-green-500/90"
                : event.status === "suspended" ? "bg-amber-500/90"
                  : event.status === "cancelled" ? "bg-red-500/90"
                    : "bg-blue-500/90"
            }`}>
              {event.status}
            </span>
          )}
        </div>
      </div>

      <section className="mx-auto max-w-5xl px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left: Event info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="font-display text-3xl font-bold">{event.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">by {event.organizer}</p>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <Calendar className="size-4 text-primary" />
                <span className="text-sm font-medium">
                  {dateObj.toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
              {event.time && (
                <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                  <Clock className="size-4 text-primary" />
                  <span className="text-sm font-medium">{event.time}</span>
                </div>
              )}
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <MapPin className="size-4 text-primary" />
                <span className="text-sm font-medium">{event.venueName}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <Users className="size-4 text-primary" />
                <span className="text-sm font-medium">{event.totalRegistrations} registered</span>
              </div>
            </div>

            <p className="text-foreground/80 leading-relaxed">{event.description}</p>

            {/* Ticket tiers */}
            <div>
              <h3 className="font-display text-lg font-semibold mb-3">Ticket Tiers</h3>
              <div className="space-y-3">
                {event.tiers?.map((tier: EventTierResponse) => {
                  const available = tier.capacity - tier.sold;
                  const pct = Math.round((tier.sold / tier.capacity) * 100);
                  const isSoldOut = tier.status.toLowerCase() === "sold_out";
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      disabled={isSoldOut}
                      onClick={() => !isSoldOut && setSelectedTierId(tier.id)}
                      className={`w-full rounded-2xl border-2 p-4 text-left transition-all ${
                        selectedTierId === tier.id
                          ? "border-primary shadow-sm " + (TIER_COLORS[tier.tierType] || "bg-accent/30")
                          : isSoldOut
                            ? "border-border/30 bg-muted/20 opacity-50 cursor-not-allowed"
                            : "border-border/50 hover:border-primary/40 " + (TIER_COLORS[tier.tierType] || "")
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TIER_BADGE[tier.tierType] || "bg-muted"}`}>{TIER_LABELS[tier.tierType] || tier.tierType}</span>
                          <span className="font-semibold">{tier.name}</span>
                        </div>
                        <span className="font-display text-lg font-bold text-primary">{formatNaira(tier.price)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                        <span>{isSoldOut ? "Sold out" : `${available} of ${tier.capacity} remaining`}</span>
                        <span>{pct}% sold</span>
                      </div>
                      <progress
                        className="h-1.5 w-full overflow-hidden rounded-full bg-border/50 text-primary [&::-webkit-progress-bar]:bg-border/50 [&::-webkit-progress-value]:bg-primary [&::-webkit-progress-value]:rounded-full [&::-moz-progress-bar]:bg-primary"
                        value={tier.sold}
                        max={tier.capacity || 1}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Registration card (guests) or Event Stats (staff) */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 rounded-2xl border border-border/60 bg-card p-5 shadow-md space-y-4">
              {isStaff ? (
                <>
                  <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <BarChart3 className="size-5 text-primary" /> Event Stats
                  </h3>
                  {!isActive && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                      This event is <strong>{event.status}</strong> and is not visible to the public.
                    </div>
                  )}

                  {/* Revenue per tier */}
                  <div className="space-y-2.5">
                    {event.tiers?.map((tier: EventTierResponse) => {
                      const revenue = tier.price * tier.sold;
                      return (
                        <div key={tier.id} className={`rounded-xl border p-3 text-sm ${TIER_COLORS[tier.tierType] || "bg-accent/30 border-border"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{tier.name}</span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/60">{TIER_LABELS[tier.tierType] || tier.tierType}</span>
                          </div>
                          <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                            <span>{tier.sold} sold / {tier.capacity} total</span>
                            <span className="font-semibold text-foreground">{formatNaira(revenue)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals */}
                  <div className="rounded-xl bg-accent/40 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground"><Users className="size-3.5" /> Total registrations</span>
                      <span className="font-semibold">{event.totalRegistrations}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground"><Banknote className="size-3.5" /> Total revenue</span>
                      <span className="font-semibold text-primary">
                        {formatNaira(event.tiers?.reduce((sum: number, t: EventTierResponse) => sum + t.price * t.sold, 0) ?? 0)}
                      </span>
                    </div>
                  </div>

                  {isBusinessOwner && event.status !== "cancelled" && event.status !== "suspended" && (
                    <Button variant="outline" className="w-full rounded-full" size="sm" onClick={() => setIsEditing(true)}>
                      Edit Event
                    </Button>
                  )}
                </>
              ) : !isActive ? (
                <div className="text-center py-4">
                  <Ticket className="size-10 text-muted-foreground/30 mx-auto mb-2" />
                  <h3 className="font-display text-base font-semibold">Not Available</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This event is <strong>{event.status}</strong> and cannot be booked yet.
                  </p>
                </div>
              ) : isEventSoldOut ? (
                <div className="text-center py-4">
                  <Ticket className="size-10 text-red-400/40 mx-auto mb-2" />
                  <h3 className="font-display text-base font-semibold text-red-600">Sold Out</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    All ticket tiers for this event have been sold out.
                  </p>
                </div>
              ) : !isAuthenticated ? (
                <div className="text-center py-6 space-y-4">
                  <Ticket className="size-10 text-muted-foreground/30 mx-auto mb-2" />
                  <h3 className="font-display text-base font-semibold">Sign in to Get Tickets</h3>
                  <p className="text-sm text-muted-foreground">
                    You need to be logged in to register for events.
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button asChild size="sm">
                      <Link to="/login">Sign In</Link>
                    </Button>
                    <Button variant="outline" asChild size="sm">
                      <Link to="/register">Create Account</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-lg font-semibold">Get Tickets</h3>
                  <TestModeBanner />

                  {selectedTier ? (
                    <div className={`rounded-xl border p-3 text-sm ${TIER_COLORS[selectedTier.tierType] || "bg-accent/30 border-border"}`}>
                      <p className="font-semibold">{selectedTier.name}</p>
                      <p className="text-muted-foreground">{formatNaira(selectedTier.price)} per ticket</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Select a ticket tier above</p>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Quantity</Label>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))} className="size-8 rounded-full border flex items-center justify-center hover:bg-accent text-sm font-bold">−</button>
                      <span className="w-8 text-center font-semibold">{qty}</span>
                      <button onClick={() => setQty(q => Math.min(10, q + 1))} className="size-8 rounded-full border flex items-center justify-center hover:bg-accent text-sm font-bold">+</button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Full Name</Label>
                      <Input placeholder="Your name" value={form.name} readOnly={isAuthenticated} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Email</Label>
                      <Input type="email" placeholder="your@email.com" value={form.email} readOnly={isAuthenticated} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    </div>
                  </div>

                  {total > 0 && (
                    <div className="flex items-center justify-between rounded-xl bg-accent/40 px-3 py-2 text-sm font-semibold">
                      <span>Total</span>
                      <span className="text-primary">{formatNaira(total)}</span>
                    </div>
                  )}

                  {/* Payment method selection for authenticated users */}
                  {isAuthenticated && total > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Payment Method</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("wallet")}
                          className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm transition-all ${
                            paymentMethod === "wallet"
                              ? "border-primary bg-primary/5"
                              : "border-border/50 hover:border-primary/30"
                          }`}
                        >
                          <Wallet className="size-4 text-primary" />
                          <div className="text-left">
                            <p className="font-medium text-xs">Wallet</p>
                            <p className="text-[10px] text-muted-foreground">
                              {walletBalance !== null ? formatNaira(walletBalance) : "Loading…"}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("bank_transfer")}
                          className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm transition-all ${
                            paymentMethod === "bank_transfer"
                              ? "border-primary bg-primary/5"
                              : "border-border/50 hover:border-primary/30"
                          }`}
                        >
                          <CreditCard className="size-4 text-primary" />
                          <div className="text-left">
                            <p className="font-medium text-xs">Bank Transfer</p>
                            <p className="text-[10px] text-muted-foreground">Pay to virtual account</p>
                          </div>
                        </button>
                      </div>
                      {paymentMethod === "wallet" && walletBalance !== null && walletBalance < total && (
                        <p className="text-xs text-red-600">Insufficient wallet balance. Please top up or use bank transfer.</p>
                      )}
                    </div>
                  )}

                  <Button className="w-full" size="lg" onClick={handleRegister} disabled={registering || !selectedTierId || isEventSoldOut || (paymentMethod === "wallet" && walletBalance !== null && walletBalance < total)}>
                    {registering ? "Processing…" : isEventSoldOut ? "Sold Out" : `Register${total > 0 ? ` · ${formatNaira(total)}` : ""}`}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">Secure checkout · Instant confirmation</p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <Outlet />

      <Sheet open={isEditing} onOpenChange={setIsEditing}>
        <SheetContent className="p-0 flex flex-col h-full sm:max-w-md w-full">
          {/* Header */}
          <div className="p-6 border-b border-border/60 bg-card">
            <SheetTitle className="font-display text-lg font-bold">Edit Event</SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground mt-1">
              Update event details and save changes directly from this page.
            </SheetDescription>
          </div>

          {/* Form Content (Scrollable) */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Event Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter event name" />
            </div>

            {/* Organizer */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Organizer</Label>
              <Input value={editForm.organizer} onChange={e => setEditForm(f => ({ ...f, organizer: e.target.value }))} placeholder="Who is organizing this event?" />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Description</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Tell attendees what to expect…" rows={3} />
            </div>

            {/* Date & Time */}
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium flex items-center gap-1"><Calendar className="size-3.5" /> Date</Label>
                <Input type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium flex items-center gap-1"><Clock className="size-3.5" /> Time</Label>
                <Input type="time" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} />
              </div>
            </div>

            {/* Venue */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1"><MapPin className="size-3.5" /> Venue</Label>
              {venuesLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Loader2 className="size-4 animate-spin" /> Loading venues…</div>
              ) : (
                <select
                  aria-label="Venue"
                  value={editForm.venueId}
                  onChange={e => {
                    const vId = e.target.value;
                    const v = venues.find(x => x.id === vId);
                    setEditForm(f => ({ ...f, venueId: vId, venueName: v?.name ?? f.venueName }));
                  }}
                  className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Custom venue name</option>
                  {venues.map(v => (
                    <option key={v.id} value={v.id}>{v.name} — {v.location}</option>
                  ))}
                </select>
              )}
              {(!editForm.venueId || !venues.find(v => v.id === editForm.venueId)) && (
                <Input value={editForm.venueName} onChange={e => setEditForm(f => ({ ...f, venueName: e.target.value }))} placeholder="Venue name" className="mt-2" />
              )}
            </div>

            {/* Thumbnail */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Event Thumbnail</Label>
              {editForm.image ? (
                <div className="relative rounded-lg border border-border/60 overflow-hidden w-48 aspect-[16/9] mx-auto bg-muted">
                  <img src={resolveMediaUrl(editForm.image)} alt="Event" className="size-full object-cover" />
                  <button
                    type="button"
                    aria-label="Remove thumbnail"
                    onClick={() => setEditForm(f => ({ ...f, image: "" }))}
                    className="absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </div>
              ) : (
                <Input type="file" accept="image/*" onChange={handleEditImageUpload} className="text-xs h-9 file:text-xs" />
              )}
            </div>

            {/* Ticket Tiers */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-sm font-semibold flex items-center gap-2">
                  <Ticket className="size-4 text-primary" /> Ticket Tiers
                </h3>
              </div>
              <p className="text-[11px] text-muted-foreground">Edit tier names, category, prices and capacity. New tiers cannot be added and tiers with tickets sold cannot be removed.</p>

              <div className="space-y-3">
                {editTiers.map((tier, idx) => (
                  <div key={tier.id ?? `new-${idx}`} className="rounded-xl border border-border/50 bg-accent/10 p-3 space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-muted-foreground">Tier {idx + 1}</span>
                      {tier.sold === 0 && (
                        <button type="button" aria-label={`Remove tier ${idx + 1}`} onClick={() => removeEditTier(idx)}
                          className="p-1 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                          <Trash2 className="size-3" />
                        </button>
                      )}
                    </div>
                    <div className="grid gap-2.5 grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-medium">Name</Label>
                        <Input value={tier.name} onChange={e => updateEditTier(idx, "name", e.target.value)} placeholder="e.g. VIP" className="h-8 text-xs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-medium">Category</Label>
                        <select
                          aria-label={`Category for tier ${idx + 1}`}
                          value={tier.tierType}
                          onChange={e => updateEditTier(idx, "tierType", e.target.value)}
                          className="w-full rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                          {Object.entries(TIER_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-medium">Price (₦)</Label>
                        <Input type="number" min={0} step={500} value={tier.price} onChange={e => updateEditTier(idx, "price", e.target.value)} className="h-8 text-xs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-medium">Capacity</Label>
                        <Input type="number" min={1} value={tier.capacity} onChange={e => updateEditTier(idx, "capacity", e.target.value)} className="h-8 text-xs" />
                      </div>
                    </div>
                    {tier.sold > 0 && (
                      <p className="text-[9px] text-muted-foreground">
                        {tier.sold} tickets sold. Capacity must remain ≥ {tier.sold}.
                      </p>
                    )}
                    {tier.name && Number(tier.price) > 0 && Number(tier.capacity) > 0 && (
                      <div className="rounded-lg bg-background/80 border border-border/40 px-2 py-1 text-[10px] text-muted-foreground">
                        <span className="font-medium text-foreground">{tier.name}</span>
                        {" · "}{formatNaira(Number(tier.price))} × {tier.capacity}
                        {" · "}Max revenue: <span className="font-semibold text-primary">{formatNaira(Number(tier.price) * Number(tier.capacity))}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer (Pinned) */}
          <div className="p-6 border-t border-border/60 bg-background flex items-center gap-3">
            <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1 rounded-full text-xs h-9">
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={submitting} className="flex-1 gap-1.5 rounded-full text-xs h-9">
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              Save changes
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
